import { t } from "@hakili/i18n";
import {
  dealerDocumentsLenientReadSchema,
  type DealerDocumentView,
  type DealerDocumentsAnswer,
} from "@hakili/contracts";

/**
 * `DEALER-V2-B1` (`P1-DOS-077`): **the dealer's pièces door, as the screen reads it.** The answer is read through the contract's LENIENT schema (a newer door may add a key, a required key missing is not an
 * answer). **Nothing here counts and nothing here composes a word:** the bar is the served `summary`, the months and the reading are the server's sentences, the missing months are the server's list.
 */
export type DealerDocumentsRead =
  { readonly ok: true; readonly answer: DealerDocumentsAnswer } | { readonly ok: false };

export function readDealerDocuments(raw: unknown): DealerDocumentsRead {
  const parsed = dealerDocumentsLenientReadSchema.safeParse(raw);
  return parsed.success ? { ok: true, answer: parsed.data } : { ok: false };
}

export interface DealerDocumentsSeam {
  readonly request: <T>(
    path: "/api/v1/me/dealership/dossiers/{id}/pieces",
    method: "get",
    options: { readonly params: { readonly id: string } },
  ) => Promise<T>;
}

/** `P1-DOS-079`: the one place dealer-web reads the dealer's pièces door. {id} is a dossier id from the dealer's own list; a failure carries the door's sentence. */
export async function readDealerDocumentsDoor(
  api: DealerDocumentsSeam,
  dossierId: string,
): Promise<
  | { readonly ok: true; readonly value: DealerDocumentsAnswer }
  | { readonly ok: false; readonly message: string }
> {
  try {
    const raw = await api.request<unknown>("/api/v1/me/dealership/dossiers/{id}/pieces", "get", {
      params: { id: dossierId },
    });
    const read = readDealerDocuments(raw);
    return read.ok
      ? { ok: true, value: read.answer }
      : { ok: false, message: t("common.error.reponseInattendue") };
  } catch (failure) {
    const message = (failure as { message?: unknown } | null)?.message;
    return {
      ok: false,
      message:
        failure instanceof TypeError || typeof message !== "string" || message === ""
          ? t("common.error.connexionEchouee")
          : message,
    };
  }
}

export interface DocumentsView {
  /** The two served numbers, as served. */
  readonly bar: { readonly ready: number; readonly expected: number };
  /** The buyer's pièces (the car's and the « sans objet » apart): the ones not ready first, each group in the served order. */
  readonly buyer: readonly DealerDocumentView[];
  /** The car's pièces, read only, in the served order. */
  readonly car: readonly DealerDocumentView[];
  /** The first pièce not ready, in the served order; the one primary action stands on it alone. `null` when every pièce is. */
  readonly nextMissing: string | null;
  /** True exactly when the served summary says ready reaches expected: the bar never disagrees with the send guard. */
  readonly allReady: boolean;
}

/**
 * `P1-DLW-053` (designer R, batch 2): **what Hakili's strip says, from the served states only.** `flagging` when a pièce is rejected (`rejetee`, or the reading says `illisible`) or the reading found months missing
 * (`missingMonths` served and not empty); `done` only when at least one pièce was read, every pièce read was found complete, nothing is signalled and nothing is still under reading; otherwise `null`: no strip. « Il lit » is
 * not served and is never a state here. The names are the pièces' own served `shortLabel`, in the served order; nothing is composed or joined.
 */
export type DocumentsStrip = {
  readonly state: "flagging" | "done";
  readonly names: readonly string[];
} | null;

export function documentsStripOf(answer: DealerDocumentsAnswer): DocumentsStrip {
  const drawn = answer.pieces.filter((one) => one.status !== "sansObjet" && one.vehicle === null);
  const flagged = drawn.filter(
    (one) => one.status === "rejetee" || one.reading === "illisible" || (one.missingMonths?.length ?? 0) > 0,
  );
  if (flagged.length > 0) return { state: "flagging", names: flagged.map((one) => one.shortLabel) };
  if (drawn.some((one) => one.reading === "en_lecture")) return null;
  // « Terminé » speaks of the MONTHS the reading found (`readMonths` served, nothing missing): a pièce Hakili read without months (the identity, « Lue par Hakili ») is not a finding and names nothing here.
  const read = drawn.filter(
    (one) => one.readMonths !== null && one.missingMonths !== null && one.readMonths.length > 0,
  );
  return read.length === 0 ? null : { state: "done", names: read.map((one) => one.shortLabel) };
}

export function documentsViewOf(answer: DealerDocumentsAnswer): DocumentsView {
  const drawn = answer.pieces.filter((one) => one.status !== "sansObjet");
  const buyerAll = drawn.filter((one) => one.vehicle === null);
  const notReady = buyerAll.filter((one) => !one.ready);
  const ready = buyerAll.filter((one) => one.ready);
  return {
    bar: { ready: answer.summary.ready, expected: answer.summary.expected },
    buyer: [...notReady, ...ready],
    car: drawn.filter((one) => one.vehicle !== null),
    nextMissing: notReady[0]?.code ?? null,
    allReady: answer.summary.ready >= answer.summary.expected,
  };
}
